/**
 * Recovery Score formula (BW18 PR-E + BW18.5 PR-5.B research correction).
 *
 * v2 weights and overrides land per the research report at
 * `~/Documents/Claude/Projects/Boston Relocation/haus-spec/research/
 * RESEARCH-RESPONSE-CONSOLIDATED-v0.1.md` §1.6 + §1.7. The central design
 * rule (CLAUDE.md): never let a downstream signal override an upstream
 * risk. Hard caps on EA<30 / EA<25 and illness signals enforce this.
 *
 * Validated against ISSN 2018 (HRV), AASM 2015 (sleep duration), and the
 * IOC/ISSN RED-S consensus 2023 (energy availability thresholds).
 */

export type RecoveryWeights = {
  hrv: number;
  sleep: number;
  rhr: number;
  load: number;
  ea: number;
  subjective: number;
};

export const DEFAULT_WEIGHTS: RecoveryWeights = {
  hrv: 0.25,
  sleep: 0.2,
  rhr: 0.1,
  load: 0.15,
  ea: 0.25,
  subjective: 0.05,
};

export type IllnessSignals = {
  elevated_resp_rate: boolean;
  elevated_wrist_temp: boolean;
  fever_or_sore_throat: boolean;
};

export type RecoveryInputs = {
  // HRV. v2 uses ln(rmssd) 7-day rolling vs 28-day baseline.
  hrv_7d_ln_rmssd: number | null;
  hrv_baseline_mean: number | null;
  hrv_baseline_sd: number | null;
  // Legacy v1 fallbacks — used only when v2 inputs are absent.
  hrv_last_night_ms: number | null;
  hrv_baseline_samples: number[];

  // Sleep. v2 uses last-night hours + 7d average + accumulated debt.
  sleep_hours: number | null;
  sleep_target_hours: number;
  sleep_7d_avg_h: number | null;
  sleep_debt_7d_h: number | null;

  // RHR. v2 uses 7-day average vs 28-day baseline.
  rhr_7d_bpm: number | null;
  rhr_baseline_mean: number | null;
  rhr_baseline_sd: number | null;
  rhr_last_night_bpm: number | null;
  rhr_baseline_samples: number[];

  // Training load. 7d acute / 28d chronic ratio.
  training_load_7d: number | null;
  training_load_28d_avg: number | null;

  // Energy availability inputs. v2 takes min(yesterday, 3d_avg) so the
  // worst recent day surfaces in the score.
  kcal_in_yesterday: number | null;
  kcal_workout_yesterday: number | null;
  ea_3d_avg_kcal_per_kg_ffm: number | null;
  fat_free_mass_kg: number | null;

  // Subjective readiness 1-5 from the daily check-in.
  subjective_readiness: number | null;
  subjective_soreness: number | null;
  illness_signals: IllnessSignals | null;
};

export type RecoveryComponents = {
  hrv_z_component: number | null;
  sleep_component: number | null;
  rhr_z_component: number | null;
  load_component: number | null;
  ea_component: number | null;
  subjective_component: number | null;
  ea_kcal_per_kg_ffm: number | null;
  ea_status: EaStatus | null;
  override_reason: OverrideReason | null;
};

export type OverrideReason = "ea_problematic" | "ea_risk" | "illness";
export type EaStatus = "optimal" | "adequate" | "risk" | "problematic";

export type RecoveryResult = RecoveryComponents & {
  score: number;
  formula_version: "v2";
  available_components: number;
  data_quality: DataQuality;
};

export type DataQuality = "sufficient" | "partial" | "insufficient";

export const MIN_COMPONENTS_FOR_SCORE = 3;

const tanh = (x: number) => Math.tanh(x);

function meanStd(samples: number[]): { mean: number; std: number } {
  if (samples.length === 0) return { mean: 0, std: 1 };
  const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
  const variance =
    samples.reduce((a, b) => a + (b - mean) ** 2, 0) / samples.length;
  const std = Math.sqrt(variance) || 1;
  return { mean, std };
}

export function classifyEa(ea: number): EaStatus {
  if (ea >= 45) return "optimal";
  if (ea >= 30) return "adequate";
  if (ea >= 25) return "risk";
  return "problematic";
}

function eaToComponent(ea: number): number {
  if (ea >= 45) return 100;
  if (ea >= 30) return 70 + ((ea - 30) / 15) * 30;
  if (ea >= 25) return 30 + ((ea - 25) / 5) * 40;
  return Math.max(0, (ea / 25) * 30);
}

/**
 * Training load component. The acute/chronic ratio (Banister-Calvert
 * approach) maps to four zones:
 *   <0.8  : under-loading                (component 80)
 *   0.8-1.3: optimal                     (100)
 *   1.3-1.5: ramping (caution)           (70)
 *   >=1.5  : overreaching                (40)
 */
function loadToComponent(acute: number, chronic: number): number {
  if (chronic <= 0) return 70;
  const ratio = acute / chronic;
  if (ratio < 0.8) return 80;
  if (ratio <= 1.3) return 100;
  if (ratio <= 1.5) return 70;
  return 40;
}

export function computeRecovery(
  inputs: RecoveryInputs,
  weights: RecoveryWeights = DEFAULT_WEIGHTS,
): RecoveryResult {
  // HRV component. Prefer v2 (ln rmssd 7d vs baseline) and fall back to
  // last-night vs 28-day samples for legacy data.
  let hrv_z_component: number | null = null;
  if (
    inputs.hrv_7d_ln_rmssd != null &&
    inputs.hrv_baseline_mean != null &&
    inputs.hrv_baseline_sd != null &&
    inputs.hrv_baseline_sd > 0
  ) {
    const z =
      (inputs.hrv_7d_ln_rmssd - inputs.hrv_baseline_mean) /
      inputs.hrv_baseline_sd;
    hrv_z_component = 50 + 50 * tanh(z / 1.5);
    hrv_z_component = Math.max(0, Math.min(100, hrv_z_component));
  } else if (
    inputs.hrv_last_night_ms != null &&
    inputs.hrv_baseline_samples.length >= 7
  ) {
    const { mean, std } = meanStd(inputs.hrv_baseline_samples);
    const z = (inputs.hrv_last_night_ms - mean) / std;
    hrv_z_component = 50 + 50 * tanh(z / 1.5);
    hrv_z_component = Math.max(0, Math.min(100, hrv_z_component));
  }

  // Sleep component. Last-night fraction-of-target, minus a sleep-debt
  // penalty (up to 35) and a chronic short-sleep penalty (10 if 7d avg
  // below 6.5h).
  let sleep_component: number | null = null;
  if (inputs.sleep_hours != null) {
    const base =
      Math.min(1, inputs.sleep_hours / inputs.sleep_target_hours) * 100;
    const debt = Math.max(0, inputs.sleep_debt_7d_h ?? 0);
    const debtPenalty = Math.min(35, debt * 5);
    const chronicShortPenalty =
      inputs.sleep_7d_avg_h != null && inputs.sleep_7d_avg_h < 6.5 ? 10 : 0;
    sleep_component = Math.max(
      0,
      Math.min(100, base - debtPenalty - chronicShortPenalty),
    );
  }

  // RHR component. Lower-is-better. v2 uses 7d vs baseline; legacy fallback
  // to last-night vs 28-day samples.
  let rhr_z_component: number | null = null;
  if (
    inputs.rhr_7d_bpm != null &&
    inputs.rhr_baseline_mean != null &&
    inputs.rhr_baseline_sd != null &&
    inputs.rhr_baseline_sd > 0
  ) {
    const z =
      (inputs.rhr_7d_bpm - inputs.rhr_baseline_mean) / inputs.rhr_baseline_sd;
    rhr_z_component = 50 - 50 * tanh(z / 1.5);
    rhr_z_component = Math.max(0, Math.min(100, rhr_z_component));
  } else if (
    inputs.rhr_last_night_bpm != null &&
    inputs.rhr_baseline_samples.length >= 7
  ) {
    const { mean, std } = meanStd(inputs.rhr_baseline_samples);
    const z = (inputs.rhr_last_night_bpm - mean) / std;
    rhr_z_component = 50 - 50 * tanh(z / 1.5);
    rhr_z_component = Math.max(0, Math.min(100, rhr_z_component));
  }

  // Training load — acute/chronic ratio.
  let load_component: number | null = null;
  if (inputs.training_load_7d != null && inputs.training_load_28d_avg != null) {
    load_component = loadToComponent(
      inputs.training_load_7d,
      inputs.training_load_28d_avg,
    );
  }

  // EA component. v2 takes min(yesterday, 3d_avg) so a single bad day
  // can't be smoothed away by a strong week.
  let ea_component: number | null = null;
  let ea_kcal_per_kg_ffm: number | null = null;
  let ea_status: EaStatus | null = null;
  let override_reason: OverrideReason | null = null;

  if (
    inputs.kcal_in_yesterday != null &&
    inputs.kcal_workout_yesterday != null &&
    inputs.fat_free_mass_kg &&
    inputs.fat_free_mass_kg > 0
  ) {
    const eaYesterday =
      (inputs.kcal_in_yesterday - inputs.kcal_workout_yesterday) /
      inputs.fat_free_mass_kg;
    const ea3d = inputs.ea_3d_avg_kcal_per_kg_ffm;
    const eaForScoring =
      ea3d != null ? Math.min(eaYesterday, ea3d) : eaYesterday;
    ea_kcal_per_kg_ffm = Math.round(eaForScoring * 10) / 10;
    ea_component = eaToComponent(eaForScoring);
    ea_status = classifyEa(eaForScoring);
    if (eaForScoring < 25) override_reason = "ea_problematic";
    else if (eaForScoring < 30) override_reason = "ea_risk";
  }

  // Subjective readiness. Default to 70 if unanswered so the score isn't
  // null-out by users skipping the daily check-in.
  let subjective_component: number | null = null;
  if (inputs.subjective_readiness != null) {
    subjective_component = (inputs.subjective_readiness / 5) * 100;
  } else {
    subjective_component = 70;
  }

  // Illness override
  if (inputs.illness_signals) {
    if (
      inputs.illness_signals.elevated_resp_rate ||
      inputs.illness_signals.elevated_wrist_temp ||
      inputs.illness_signals.fever_or_sore_throat
    ) {
      if (override_reason !== "ea_problematic") {
        override_reason = "illness";
      }
    }
  }

  // Weighted composite. Missing components renormalize across the rest;
  // a single low-RHR signal can't synthesize panic from missing data.
  const components: Array<[number | null, number]> = [
    [hrv_z_component, weights.hrv],
    [sleep_component, weights.sleep],
    [rhr_z_component, weights.rhr],
    [load_component, weights.load],
    [ea_component, weights.ea],
    [subjective_component, weights.subjective],
  ];

  let score = 0;
  let totalWeight = 0;
  let availableComponents = 0;
  for (const [value, weight] of components) {
    if (value == null) continue;
    score += value * weight;
    totalWeight += weight;
    availableComponents += 1;
  }
  if (totalWeight > 0) score = score / totalWeight;
  else score = 50;

  // Hard overrides per the central design rule.
  if (override_reason === "ea_problematic") score = Math.min(score, 40);
  else if (override_reason === "ea_risk") score = Math.min(score, 55);
  if (override_reason === "illness") score = Math.min(score, 50);

  const dataQuality: DataQuality =
    availableComponents >= MIN_COMPONENTS_FOR_SCORE
      ? availableComponents >= 4
        ? "sufficient"
        : "partial"
      : "insufficient";

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    hrv_z_component:
      hrv_z_component != null ? Math.round(hrv_z_component) : null,
    sleep_component: sleep_component != null ? Math.round(sleep_component) : null,
    rhr_z_component:
      rhr_z_component != null ? Math.round(rhr_z_component) : null,
    load_component: load_component != null ? Math.round(load_component) : null,
    ea_component: ea_component != null ? Math.round(ea_component) : null,
    subjective_component:
      subjective_component != null ? Math.round(subjective_component) : null,
    ea_kcal_per_kg_ffm,
    ea_status,
    override_reason,
    formula_version: "v2",
    available_components: availableComponents,
    data_quality: dataQuality,
  };
}

export type ExertionZone = {
  band: "rest" | "easy" | "moderate" | "quality" | "push";
  color: "red" | "orange" | "yellow" | "light-green" | "green";
  recommendation: string;
};

export function exertionZone(score: number): ExertionZone {
  if (score < 30) {
    return {
      band: "rest",
      color: "red",
      recommendation:
        "State: signals point to high recovery debt. Meaning: a hard session today risks compounding it. Next move: rest or 20-minute easy walk.",
    };
  }
  if (score < 50) {
    return {
      band: "easy",
      color: "orange",
      recommendation:
        "State: recovery in the easy band. Meaning: aerobic stress is manageable but intensity is not. Next move: zone 2 only, 30-45 min, protein-priority breakfast.",
    };
  }
  if (score < 70) {
    return {
      band: "moderate",
      color: "yellow",
      recommendation:
        "State: recovery in the moderate band. Meaning: a steady aerobic session is supported. Next move: 45-75 min moderate effort with full fueling.",
    };
  }
  if (score < 85) {
    return {
      band: "quality",
      color: "light-green",
      recommendation:
        "State: recovery in the quality band. Meaning: high-quality work is supported. Next move: threshold or intervals, 60-75 min, with fueling.",
    };
  }
  return {
    band: "push",
    color: "green",
    recommendation:
      "State: recovery in the push band. Meaning: a peak performance day is on the table. Next move: race, time-trial, or testing if on the calendar.",
  };
}
